import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { editionsAPI, eventsAPI, speakersAPI, seedAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { ArrowRight, MapPin, Calendar, Users, Rocket, Sparkles, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useSEO } from '../../hooks/useSEO';
import { useIsMobile } from '../../hooks/useIsMobile';

const FLUID_SHAPE = "/assets/hero-fluid-shape.png";
const LOGO_GRADIENT = "/assets/sios-logo.png";

export const HomePage = () => {
  useSEO('Home');
  const isMobile = useIsMobile();
  const [edition, setEdition] = useState(null);
  const [events, setEvents] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Countdown effect
  useEffect(() => {
    if (!nextEvent?.date) return;
    
    const calculateCountdown = () => {
      const eventDate = new Date(nextEvent.date);
      eventDate.setHours(9, 0, 0, 0);
      const now = new Date();
      const diff = eventDate - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds });
    };
    
    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, [nextEvent?.date]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Try to get data, if empty seed the database
      const editionsRes = await editionsAPI.getAll('publish');
      
      if (editionsRes.data.length === 0) {
        // Seed database
        await seedAPI.seed();
        toast.success('Database inizializzato con dati demo');
        // Reload
        const newEditionsRes = await editionsAPI.getAll('publish');
        if (newEditionsRes.data.length > 0) {
          // Find edition for current year
          const currentYear = new Date().getFullYear();
          const currentEdition = newEditionsRes.data.find(e => e.year === currentYear) 
            || newEditionsRes.data.find(e => e.year === currentYear + 1)
            || newEditionsRes.data[0];
          setEdition(currentEdition);
          
          // Load events only for current edition
          const eventsRes = await eventsAPI.getAll({ edition_id: currentEdition.id, status: 'publish' });
          setEvents(eventsRes.data);
          findNextEvent(eventsRes.data);
        }
      } else {
        // Find edition for current year
        const currentYear = new Date().getFullYear();
        const currentEdition = editionsRes.data.find(e => e.year === currentYear) 
          || editionsRes.data.find(e => e.year === currentYear + 1)
          || editionsRes.data[0];
        setEdition(currentEdition);
        
        // Load events only for current edition
        const eventsRes = await eventsAPI.getAll({ edition_id: currentEdition.id, status: 'publish' });
        setEvents(eventsRes.data);
        findNextEvent(eventsRes.data);
      }

      const speakersRes = await speakersAPI.getHomepage();
      setSpeakers(speakersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Find the most imminent event (next in time) WITH a hero image.
  // An event is considered "current" until the END of its end_date (or date, fallback)
  // so it stays featured for the full last day.
  const findNextEvent = (eventsData) => {
    if (!eventsData || eventsData.length === 0) return;

    const now = new Date();

    // Returns the moment after which the event is no longer "current"
    // = end_date (or date) at 23:59:59.999 local time
    const effectiveEnd = (event) => {
      const ref = event.end_date || event.date;
      if (!ref) return new Date(0);
      const d = new Date(ref);
      d.setHours(23, 59, 59, 999);
      return d;
    };

    // Sort events by start date
    const sortedEvents = [...eventsData].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });

    // First upcoming/current event with a hero image
    const upcomingWithImage = sortedEvents.find(
      event => effectiveEnd(event) >= now && event.hero_image
    );

    // Fallback to any upcoming/current event, or any event with image
    const upcoming = upcomingWithImage
      || sortedEvents.find(event => effectiveEnd(event) >= now)
      || sortedEvents.find(event => event.hero_image)
      || sortedEvents[0];

    setNextEvent(upcoming);
  };

  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  // True if today's date falls within the event window [date .. end_date]
  const isEventToday = (event) => {
    if (!event?.date) return false;
    const startStr = event.date;
    const endStr = event.end_date || event.date;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startStr);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endStr);
    end.setHours(23, 59, 59, 999);
    return today >= start && today <= end;
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
          <Rocket className="text-primary animate-bounce" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden homepage-text" data-testid="home-page">
      {/* Hero Section with Next Event */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        {nextEvent?.hero_image && (
          <>
            <img
              src={getFullImageUrl(isMobile && nextEvent.hero_image_mobile ? nextEvent.hero_image_mobile : nextEvent.hero_image)}
              alt={nextEvent.title}
              className="absolute inset-0 w-full h-full object-cover z-0"
              data-testid="home-hero-bg"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-sios-void/80 via-sios-void/40 to-transparent z-0" data-testid="home-hero-overlay" />
            {/* Mobile dark veil (darker gradient for better readability) */}
            <div className="absolute inset-0 bg-gradient-to-b from-sios-void/85 via-sios-void/80 to-sios-void/95 z-0" data-testid="home-hero-overlay-mobile" />
          </>
        )}
        
        <div className="max-w-7xl mx-auto w-full px-6 py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-mono uppercase tracking-wider text-muted-foreground">
                <Sparkles size={14} className="text-accent" />
                {edition?.year || '2025'} Edition
              </div>

              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none">
                <span className="block">StartupItalia</span>
                <span className="block text-gradient">Open Summit</span>
              </h1>

              {/* Next Event Info - Prominent - Added mt-8 for spacing */}
              {nextEvent && (
                <div className="glass rounded-2xl p-6 border border-primary/30 space-y-4 mt-8" data-testid="next-event-info">
                  <div className="flex items-center gap-2 text-primary text-sm font-mono uppercase tracking-wider">
                    <Rocket size={16} />
                    Prossimo Evento
                  </div>
                  
                  <h2 className="font-heading text-2xl md:text-3xl font-bold">
                    {nextEvent.title}
                  </h2>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Date + Time - no label */}
                    <div className="flex items-center gap-3 bg-primary/20 rounded-xl px-4 py-3">
                      <Calendar className="text-primary flex-shrink-0" size={24} />
                      <div>
                        <p className="font-heading font-semibold text-white capitalize">
                          {formatEventDate(nextEvent.date)}
                        </p>
                        {(nextEvent.event_start_time || nextEvent.event_end_time) && (
                          <p className="text-sm text-white/80">
                            {nextEvent.event_start_time && nextEvent.event_end_time 
                              ? `${nextEvent.event_start_time} - ${nextEvent.event_end_time}`
                              : nextEvent.event_start_time || nextEvent.event_end_time}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Location - no label */}
                    <div className="flex items-center gap-3 bg-secondary/20 rounded-xl px-4 py-3">
                      <MapPin className="text-secondary flex-shrink-0" size={24} />
                      <p className="font-heading font-semibold text-white">
                        {nextEvent.venue_name && nextEvent.city 
                          ? `${nextEvent.venue_name} - ${nextEvent.city}`
                          : nextEvent.venue_name || nextEvent.city || nextEvent.location}
                      </p>
                    </div>
                  </div>
                  
                  {/* Countdown OR "È OGGI" badge */}
                  {isEventToday(nextEvent) ? (
                    <div className="flex items-center gap-3 mt-4" data-testid="event-today-badge">
                      <div className="inline-flex items-center gap-3 bg-white/5 border border-primary/30 rounded-xl px-5 py-3">
                        <span className="relative inline-flex h-2.5 w-2.5 items-center justify-center">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
                        </span>
                        <p className="font-heading text-lg font-semibold text-foreground tracking-wide">
                          È OGGI
                        </p>
                      </div>
                    </div>
                  ) : (
                  <div className="flex items-center gap-3 mt-4">
                    <Clock className="text-primary" size={20} />
                    <div className="flex gap-3">
                      <div className="text-center">
                        <p className="font-heading text-2xl font-bold text-primary">{countdown.days}</p>
                        <p className="text-xs text-muted-foreground uppercase">Giorni</p>
                      </div>
                      <span className="text-2xl text-muted-foreground">:</span>
                      <div className="text-center">
                        <p className="font-heading text-2xl font-bold text-primary">{String(countdown.hours).padStart(2, '0')}</p>
                        <p className="text-xs text-muted-foreground uppercase">Ore</p>
                      </div>
                      <span className="text-2xl text-muted-foreground">:</span>
                      <div className="text-center">
                        <p className="font-heading text-2xl font-bold text-primary">{String(countdown.minutes).padStart(2, '0')}</p>
                        <p className="text-xs text-muted-foreground uppercase">Min</p>
                      </div>
                      <span className="text-2xl text-muted-foreground">:</span>
                      <div className="text-center">
                        <p className="font-heading text-2xl font-bold text-primary">{String(countdown.seconds).padStart(2, '0')}</p>
                        <p className="text-xs text-muted-foreground uppercase">Sec</p>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              )}

              {/* Buttons with mt-8 for equal spacing */}
              <div className="flex flex-wrap gap-4 mt-8">
                {nextEvent && (
                  <Link to={`/events/${nextEvent.slug}`}>
                    <Button 
                      size="lg" 
                      className="rounded-full bg-primary hover:bg-primary/90 neon-shadow-strong px-8 font-medium tracking-wide"
                      data-testid="hero-cta-event"
                    >
                      Scopri l'Evento
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            {/* Right - 3D Element (sempre viola) */}
            <div className="relative flex justify-center lg:justify-end">
              <img 
                src={FLUID_SHAPE} 
                alt="SIOS" 
                className="w-[350px] md:w-[450px] lg:w-[550px] animate-float drop-shadow-2xl"
                data-testid="hero-3d-element"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About SIOS Section */}
      <section className="py-24 px-6 bg-sios-surface/30" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Content */}
            <div className="space-y-8">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-3">
                  Cos'è il SIOS
                </p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">
                  L'Esperienza SIOS:
                </h2>
              </div>

              <div className="space-y-6 text-muted-foreground leading-relaxed">
                <p> <strong className="text-foreground">SIOS</strong> è il principale evento italiano dedicato all’innovazione, 
                al futuro del business e alle nuove tecnologie. Un appuntamento che riunisce founder, investitori, corporate e innovatori da tutta Italia. 
                Ogni edizione si sviluppa in più tappe sul territorio nazionale, offrendo un’esperienza unica di <strong class="text-secondary">networking, talk ispirazionali</strong> e sessioni pratiche di <strong class="text-accent">Masterclass e Workshop</strong>. 
                Con uno sguardo sempre più <strong className="text-foreground">AI-first</strong>, SIOS racconta e interpreta l’impatto dell’intelligenza artificiale sui modelli di business, sulle competenze e sull’innovazione di domani.
               </p>
                 <p><strong className="text-foreground">SIOS</strong> è il luogo dove nascono connessioni, si scoprono trend emergenti e si costruisce il futuro dell’innovazione italiana.  
</p>
                 
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="glass rounded-xl p-4">
                  <p className="font-heading font-bold text-2xl text-primary mb-1">+10 anni</p>
                  <p className="text-sm text-muted-foreground">Un punto di riferimento per l’innovazione italiana</p>
                </div>
                <div className="glass rounded-xl p-4">
                  <p className="font-heading font-bold text-2xl text-secondary mb-1">Community Driven</p>
                  <p className="text-sm text-muted-foreground">Connessioni, ispirazioni, occasioni per chi partecipa</p>
                </div>
                <div className="glass rounded-xl p-4">
                  <p className="font-heading font-bold text-2xl text-accent mb-1">Crossmediale</p>
                  <p className="text-sm text-muted-foreground">Powered by Startupitalia e grazie a PulltheRabbit, su tutti i canali dell'Hub MadTech</p>
                </div>
                <div className="glass rounded-xl p-4">
                  <p className="font-heading font-bold text-2xl text-green-500 mb-1">Open</p>
                  <p className="text-sm text-muted-foreground">La registrazione è gratuita e aperta a tutt*</p>
                </div>
              </div>
            </div>

            {/* Right - Visual */}
            <div className="relative">
              <div className="glass rounded-3xl p-8 space-y-6">
                <div className="flex items-center gap-4 pb-6 border-b border-white/10">
                  <div className="w-4 h-4 rounded-full bg-primary" />
                  <div className="w-4 h-4 rounded-full bg-secondary" />
                  <div className="w-4 h-4 rounded-full bg-accent" />
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center text-primary font-mono font-bold">
                      01
                    </div>
                    <div>
                      <p className="font-heading font-semibold">Main Stage</p>
                      <p className="text-sm text-muted-foreground">I temi e le voci che stanno guidando l’innovazione</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-secondary/20 flex items-center justify-center text-secondary font-mono font-bold">
                      02
                    </div>
                    <div>
                      <p className="font-heading font-semibold">Formazione</p>
                      <p className="text-sm text-muted-foreground">Workshop e masterclass per trasformare i trend in competenze</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center text-accent font-mono font-bold">
                      03
                    </div>
                    <div>
                      <p className="font-heading font-semibold">Networking</p>
                      <p className="text-sm text-muted-foreground">Incontri e connessioni tra startup, investor, corporate e innovatori</p>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-white/10">
                  <p className="text-sm text-muted-foreground">
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Moved above Events */}
      <section className="py-16 px-6" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <div className="glass rounded-3xl p-12 md:p-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              <div className="text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold text-primary mb-2">30+</p>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Tappe</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold text-secondary mb-2">100K+</p>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Partecipanti</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold text-accent mb-2">1000+</p>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Startup coinvolte</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold text-primary mb-2">100+</p>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Speaker</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      {events.length > 0 && (
        <section className="py-24 px-6 relative" data-testid="events-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">
                  Prossimi Eventi
                </p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
                  Le Tappe del {edition?.year}
                </h2>
              </div>
              <Link to="/editions">
                <Button variant="ghost" className="text-primary hover:bg-primary/10">
                  Vedi Tutte <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {events.map((event, index) => {
                const heroUrl = event.hero_image 
                  ? (event.hero_image.startsWith('http') ? event.hero_image : `${process.env.REACT_APP_BACKEND_URL}${event.hero_image}`)
                  : null;
                const cardImg = isMobile && event.hero_image_mobile
                  ? (event.hero_image_mobile.startsWith('http') ? event.hero_image_mobile : `${process.env.REACT_APP_BACKEND_URL}${event.hero_image_mobile}`)
                  : heroUrl;
                
                return (
                  <Link 
                    key={event.id} 
                    to={`/events/${event.slug}`}
                    className="group"
                    data-testid={`event-card-${index}`}
                  >
                    <article className="glass rounded-2xl overflow-hidden h-full hover:border-primary/50 transition-all duration-300 hover:neon-shadow">
                      {cardImg ? (
                        <div className="relative h-48 overflow-hidden">
                          <img 
                            src={cardImg}
                            alt={event.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                          <span className="absolute top-4 right-4 text-xs font-mono uppercase tracking-wider text-white px-3 py-1 rounded-full bg-black/50 backdrop-blur">
                            {event.date}
                          </span>
                        </div>
                      ) : (
                        <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <MapPin className="text-primary/50" size={48} />
                        </div>
                      )}
                      
                      <div className="p-6">
                        <h3 className="font-heading text-xl md:text-2xl font-semibold mb-3 group-hover:text-primary transition-colors">
                          {event.title}
                        </h3>

                        <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                          {event.description?.substring(0, 120)}...
                        </p>

                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-2">
                            <MapPin size={14} />
                            {event.location}
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Speakers Section */}
      {speakers.length > 0 && (
        <section className="py-24 px-6 bg-sios-surface/50" data-testid="speakers-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">
                  I Protagonisti
                </p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
                  Best Speaker
                </h2>
              </div>
              <Link to="/speakers">
                <Button variant="ghost" className="text-primary hover:bg-primary/10">
                  Vedi Tutti <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {speakers.map((speaker, index) => (
                <Link 
                  key={speaker.id} 
                  to={`/speakers/${speaker.slug}`}
                  className="group"
                  data-testid={`speaker-card-${index}`}
                >
                  <div className="relative aspect-[3/4] overflow-hidden rounded-xl">
                    <img 
                      src={speaker.image || `https://ui-avatars.com/api/?name=${speaker.first_name}+${speaker.last_name}&background=7B2CBF&color=fff&size=400`} 
                      alt={`${speaker.first_name} ${speaker.last_name}`}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-sios-void via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h4 className="font-heading font-semibold text-sm">
                        {speaker.first_name} {speaker.last_name}
                      </h4>
                      <p className="text-xs text-muted-foreground truncate">
                        {speaker.title && speaker.company ? `${speaker.title} - ${speaker.company}` : (speaker.title || speaker.company || '')}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 px-6 relative" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <img 
            src={LOGO_GRADIENT} 
            alt="SIOS" 
            className="h-24 md:h-32 mx-auto mb-8"
          />
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight mb-6">
            Il Futuro dell'Innovazione<br />Ti Aspetta
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Unisciti a migliaia di innovatori, founder e investitori. 
            Scopri le opportunita, costruisci connessioni, crea il futuro.
          </p>
          <Link to="/editions">
            <Button 
              size="lg" 
              className="rounded-full bg-primary hover:bg-primary/90 neon-shadow-strong px-10 font-medium tracking-wide"
              data-testid="cta-btn"
            >
              Esplora gli Eventi
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
